import "./LienPaymentHistory.scss";

import cx from "classnames";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import KaboodleToaster from "components/common/KaboodleToaster";
import ErrorState from "components/Error/ErrorState";
import { MaskedInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import type { OptionType } from "components/inputs/Select/Select";
import LoadingIcon from "components/Loading/LoadingIcon";
import Table from "components/Table/Table";
import Tooltip from "components/Tooltip/Tooltip";
import { OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCreateLienArgumentMutation,
  useDeleteLienArgumentMutation,
  useGetLienArgumentsQuery,
  useGetLienByIdQuery,
  usePatchLienByIdMutation,
} from "services/lien-api";
import {
  useCreatePaymentHistoryMutation,
  useDeletePaymentHistoryMutation,
  useGetPaymentHistoriesQuery,
  useUpdatePaymentHistoryMutation,
} from "services/payment-histories-api";
import { formatCurrency } from "utils/format-utils";
import { useConstants } from "utils/hooks";
import { useSelector } from "utils/redux";

import type {
  ArgumentTypeChoices,
  DisputeType,
  Lien,
  LienArgument,
  PaymentHistory,
} from "../LienTypes";
import AIResultsStatus from "./AIResultsStatus";
import { DisputeIcon, DisputeLegend } from "./DisputeControls";
import DisputeAllModal from "./DisputeAllModal";
import type { PaymentHistoryFormData } from "./PaymentHistoryModal";
import PaymentHistoryModal from "./PaymentHistoryModal";
import type { ReportedDiagnosisCodesFormData } from "./ReportedDiagnosisCodesModal";
import ReportedDiagnosisCodesModal from "./ReportedDiagnosisCodesModal";

const getPaymentHistoryColumns = (partType: string) => {
  const baseColumns = [
    {
      key: "disputes",
      name: "Dispute Selection",
      width: 216,
      maxWidth: 216,
      sortable: false,
    },
    {
      key: "provider_name",
      name: partType === "D" ? "Prescriber Name" : "Provider Name",
      width: "auto",
      minWidth: 200,
      sortable: false,
    },
    {
      key: "type",
      name: "Code-Type",
      width: 120,
      minWidth: 120,
      sortable: false,
    },
    {
      key: "code",
      name: "Code",
      width: 120,
      minWidth: 120,
      sortable: false,
    },
    {
      key: "description",
      name: partType === "D" ? "Drug Name" : "Description",
      width: "auto",
      minWidth: 200,
      sortable: false,
    },
    {
      key: "from_date",
      name: partType === "D" ? "Date Filled" : "From Date",
      width: "auto",
      minWidth: 100,
      sortable: false,
    },
  ];

  // Only add to_date column if not Part D
  if (partType !== "D") {
    baseColumns.push({
      key: "to_date",
      name: "To Date",
      width: "auto",
      minWidth: 100,
      sortable: false,
    });
  }

  baseColumns.push(
    {
      key: "pay_amount",
      name: "Pay",
      width: "auto",
      minWidth: 120,
      sortable: false,
    },
    {
      key: "dispute_amount",
      name: "Dispute",
      width: "auto",
      minWidth: 120,
      sortable: false,
    },
    {
      key: "relationship_score",
      name: "Relationship Score",
      width: "auto",
      minWidth: 200,
      sortable: false,
    },
  );

  return baseColumns;
};

type LienPaymentHistoryProps = {
  lien: Lien;
};

type CodeType = {
  code: string;
  description: string;
  type: string;
  relationship_score?: string;
  related_reason?: string | null;
};

type ReportedDiagnosisCodesType = {
  code: string;
  description: string;
  indicator: string;
};

type BaseRow = {
  id: string;
  rowId: number;
  type: string;
  originalRow: PaymentHistory;
};

type ParentRow = BaseRow & {
  isFirstRow: true;
  provider_name: string;
  from_date: string;
  to_date: string;
  conditional_payment: number;
  pay_amount: number;
  dispute_amount: number;
  is_disputed: boolean;
  code?: string;
  description?: string;
  relationship_score?: string;
  related_reason?: string;
};

type ChildRow = BaseRow & {
  parentId: number;
  code: string;
  description: string;
  relationship_score?: string;
  related_reason?: string;
};

type DisplayRowType = ParentRow | ChildRow;

type TableColumn = {
  key: string;
  name: string;
  width: number | string;
  minWidth?: number;
  maxWidth?: number;
  sortable: boolean;
  renderCell?: (props: { row: DisplayRowType }) => React.ReactNode;
};

const isParentRow = (row: DisplayRowType): row is ParentRow =>
  "isFirstRow" in row;
const isChildRow = (row: DisplayRowType): row is ChildRow => "parentId" in row;

const hasInvalidCodes = (row: DisplayRowType): boolean => {
  // for parent rows without a code, check if any codes are invalid
  if (isParentRow(row) && !row.code) {
    const hasInvalidICD =
      row.originalRow.diagnosis_codes_with_descriptions?.some(
        (code) =>
          code.description === "Invalid ICD code" ||
          code.description === "Missing ICD Indicator",
      );
    const hasInvalidHCPCS = row.originalRow.hcpcs_codes_with_descriptions?.some(
      (code) =>
        code.description === "Invalid HCPCS code" ||
        code.description === "Missing HCPCS Indicator",
    );
    const hasInvalidCPT = row.originalRow.cpt_codes_with_descriptions?.some(
      (code) =>
        code.description === "Invalid CPT code" ||
        code.description === "Missing CPT Indicator",
    );
    const hasInvalidDRG = row.originalRow.drg_codes_with_descriptions?.some(
      (code) =>
        code.description === "Invalid DRG code" ||
        code.description === "Missing DRG Indicator",
    );
    const hasInvalidNDC =
      row.originalRow.ndc_codes_with_descriptions?.some(
        (code) =>
          code.description === "Invalid NDC code" ||
          code.description === "Missing NDC Indicator",
      ) || false;

    return (
      hasInvalidICD ||
      hasInvalidHCPCS ||
      hasInvalidCPT ||
      hasInvalidDRG ||
      hasInvalidNDC
    );
  }

  // for rows with a specific code, only check that code type
  const codeType = row.type;
  const description = row.description || "";

  switch (codeType) {
    case "ICD-10":
      return (
        description === "Invalid ICD code" ||
        description === "Missing ICD Indicator"
      );
    case "HCPCS":
      return (
        description === "Invalid HCPCS code" ||
        description === "Missing HCPCS Indicator"
      );
    case "CPT":
      return (
        description === "Invalid CPT code" ||
        description === "Missing CPT Indicator"
      );
    case "DRG":
      return (
        description === "Invalid DRG code" ||
        description === "Missing DRG Indicator"
      );
    case "NDC":
      return (
        description === "Invalid NDC code" ||
        description === "Missing NDC Indicator"
      );
    default:
      return false;
  }
};

// from the currently selected Level of Appeal option, grab the mapped field from the lien object
const getDisputedAmountForAppealLevel = (
  lien: Lien,
  appealLevel: string,
): number => {
  if (!appealLevel) return 0;

  const mapping: Partial<Record<string, keyof Lien>> = {
    CPL: "cpl_total_disputed",
    CPN: "cpn_total_disputed",
    DEMAND: "demand_total_disputed",
    QIC: "reconsideration_amount",
    ALJ_HEARING: "omha_appeal_amount",
    MAC: "mac_appeal_amount",
    FEDERAL_DISTRICT_COURT: "federal_district_court_appeal_amount",
  };

  const field = mapping[appealLevel];
  return field ? (lien[field] as number) || 0 : 0;
};

const LienPaymentHistory = ({ lien }: LienPaymentHistoryProps) => {
  const { constants } = useConstants();
  const disputeTypes = (constants?.ArgumentTypeChoices ||
    []) as ArgumentTypeChoices[];
  const appealLevels = (constants?.LIEN_APPEAL_LEVEL_ACTIONS || []).filter(
    (option) => option.value !== null,
  );
  const showPaymentHistory = useSelector(
    (state) => state.drawer.showLienPaymentHistoryDrawer,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [
    isReportedDiagnosisCodesModalOpen,
    setIsReportedDiagnosisCodesModalOpen,
  ] = useState(false);
  const [selectedPaymentHistory, setSelectedPaymentHistory] = useState<
    PaymentHistory | undefined
  >();
  const [selectedReportedDiagnosisCodes, setSelectedReportedDiagnosisCodes] =
    useState<ReportedDiagnosisCodesType[] | undefined>();
  const [probability, setProbability] = useState<number>(95);
  const [selectedAppealLevel, setSelectedAppealLevel] = useState<string>("CPL");
  const [isDisputeAllModalOpen, setIsDisputeAllModalOpen] = useState(false);
  const [pendingDisputeData, setPendingDisputeData] = useState<{
    disputeType: DisputeType;
    paymentHistoryId: number;
  } | null>(null);
  const [page, setPage] = useState<Record<string, number>>({
    CPL: 1,
    CPN: 1,
    DEMAND: 1,
    QIC: 1,
    ALJ_HEARING: 1,
    MAC: 1,
    FEDERAL_DISTRICT_COURT: 1,
  });

  const [createPaymentHistory] = useCreatePaymentHistoryMutation();
  const [updatePaymentHistory] = useUpdatePaymentHistoryMutation();
  const [deletePaymentHistory] = useDeletePaymentHistoryMutation();
  const [createLienArgument] = useCreateLienArgumentMutation();
  const [deleteLienArgument] = useDeleteLienArgumentMutation();
  const [patchLien] = usePatchLienByIdMutation();

  const lienId = lien.id.toString();

  const { data: lienData } = useGetLienByIdQuery({ lienId }, { skip: !lienId });

  useEffect(() => {
    let selected_reported_diagnosis_codes_with_descriptions = [];

    switch (selectedAppealLevel) {
      case "CPL":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.cpl_reported_diagnosis_codes_with_descriptions || [];
        break;
      case "CPN":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.cpn_reported_diagnosis_codes_with_descriptions || [];
        break;
      case "DEMAND":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.demand_reported_diagnosis_codes_with_descriptions || [];
        break;
      case "QIC":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.qic_reported_diagnosis_codes_with_descriptions || [];
        break;
      case "ALJ_HEARING":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.alj_hearing_reported_diagnosis_codes_with_descriptions ||
          [];
        break;
      case "MAC":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.mac_reported_diagnosis_codes_with_descriptions || [];
        break;
      case "FEDERAL_DISTRICT_COURT":
        selected_reported_diagnosis_codes_with_descriptions =
          lienData?.federal_district_court_reported_diagnosis_codes_with_descriptions ||
          [];
        break;
      default:
        selected_reported_diagnosis_codes_with_descriptions = [];
    }

    if (selected_reported_diagnosis_codes_with_descriptions) {
      const mappedCodes =
        selected_reported_diagnosis_codes_with_descriptions.map((item) => {
          return {
            code: item.code,
            description: item.description || "",
            indicator: item.indicator || "0",
          };
        });

      setSelectedReportedDiagnosisCodes(mappedCodes);
    }
  }, [lienData, setSelectedReportedDiagnosisCodes, selectedAppealLevel]);

  const {
    data: paymentHistories = { results: [], count: 0 },
    isLoading: isLoadingPaymentHistories,
    isFetching,
  } = useGetPaymentHistoriesQuery(
    {
      lienId: lien.id.toString(),
      limit: PAGE_SIZE,
      offset: OFFSET(page[selectedAppealLevel]),
      levelOfAppeal: selectedAppealLevel,
    },
    {},
  );

  const { data: lienArgumentsData } = useGetLienArgumentsQuery(
    { lienId: lien?.id.toString() || "" },
    { skip: !lien?.id },
  );

  const rows = useMemo<DisplayRowType[]>(() => {
    if (!paymentHistories) return [];

    return paymentHistories.results
      .filter(
        (payment: PaymentHistory) =>
          payment.level_of_appeal === selectedAppealLevel,
      )
      .flatMap((payment: PaymentHistory) => {
        // create an array of all codes with their types and descriptions
        const allCodes: CodeType[] = [];

        const diagnosisCodes = payment.diagnosis_codes_with_descriptions;
        diagnosisCodes.forEach((code) => {
          allCodes.push({
            code: code.code,
            description: code.description || "",
            type: code.indicator === "9" ? "ICD-9" : "ICD-10",
            relationship_score: `${(code.relationship_score * 100).toString()}%`,
            related_reason: code.related_reason,
          });
        });

        const hcpcsCodes =
          payment.hcpcs_codes_with_descriptions ||
          payment.hcpcs.map((code) => ({ code, description: "" }));
        hcpcsCodes.forEach((code) => {
          allCodes.push({
            code: code.code,
            description: code.description || "",
            type: "HCPCS",
          });
        });

        const cptCodes =
          payment.cpt_codes_with_descriptions ||
          payment.cpt.map((code) => ({ code, description: "" }));
        cptCodes.forEach((code) => {
          allCodes.push({
            code: code.code,
            description: code.description || "",
            type: "CPT",
          });
        });

        const drgCodes =
          payment.drg_codes_with_descriptions ||
          payment.drg.map((code) => ({ code, description: "" }));
        drgCodes.forEach((code) => {
          allCodes.push({
            code: code.code,
            description: code.description || "",
            type: "DRG",
          });
        });

        const ndcCodes =
          payment.ndc_codes_with_descriptions ||
          (payment.ndc || []).map((code) => ({ code, description: "" }));
        ndcCodes.forEach((code) => {
          allCodes.push({
            code: code.code,
            description: code.description || "",
            type: "NDC",
          });
        });

        // check if the payment has any disputes for the current appeal level
        const hasDisputeForCurrentLevel =
          lienArgumentsData?.results?.some(
            (arg: LienArgument) => arg.payment_history === payment.id,
          ) && payment.level_of_appeal === selectedAppealLevel;

        if (allCodes.length === 0) {
          return [
            {
              id: `${payment.id}-0`,
              rowId: payment.id,
              type: "",
              originalRow: payment,
              isFirstRow: true,
              provider_name: payment.provider_name,
              from_date: payment.from_date,
              to_date: payment.to_date,
              conditional_payment: payment.conditional_payment,
              pay_amount: hasDisputeForCurrentLevel
                ? 0
                : payment.conditional_payment,
              dispute_amount: hasDisputeForCurrentLevel
                ? payment.conditional_payment
                : 0,
              is_disputed: payment.is_disputed,
              relationship_score: "",
              related_reason: "N/A",
            } as ParentRow,
          ];
        }

        // create the parent row with the first code
        const parentRow: ParentRow = {
          id: `${payment.id}-0`,
          rowId: payment.id,
          type: allCodes[0].type,
          originalRow: payment,
          isFirstRow: true,
          code: allCodes[0].code,
          description: allCodes[0].description,
          provider_name: payment.provider_name,
          from_date: payment.from_date,
          to_date: payment.to_date,
          conditional_payment: payment.conditional_payment,
          pay_amount: hasDisputeForCurrentLevel
            ? 0
            : payment.conditional_payment,
          dispute_amount: hasDisputeForCurrentLevel
            ? payment.conditional_payment
            : 0,
          is_disputed: payment.is_disputed,
          relationship_score: allCodes[0].relationship_score || "",
          related_reason: allCodes[0].related_reason || "N/A",
        };

        // create child rows for the remaining codes
        const childRows: ChildRow[] = allCodes.slice(1).map((codeInfo, i) => ({
          id: `${payment.id}-${i + 1}`,
          rowId: payment.id,
          parentId: payment.id,
          type: codeInfo.type,
          originalRow: payment,
          code: codeInfo.code,
          description: codeInfo.description,
          relationship_score: codeInfo.relationship_score || "",
          related_reason: codeInfo.related_reason || "N/A",
        }));

        return [parentRow, ...childRows];
      });
  }, [paymentHistories, lienArgumentsData, selectedAppealLevel]);

  const handleDisputeClick = useCallback(
    async (disputeType: DisputeType, paymentHistoryId?: number) => {
      if (!selectedAppealLevel && lien.part_type === "AB") {
        KaboodleToaster.error("Please select a level of appeal first");
        return;
      }

      try {
        const existingArgument = lienArgumentsData?.results?.find(
          (arg: LienArgument) =>
            arg.payment_history === paymentHistoryId &&
            arg.argument_type === disputeType,
        );

        const appealLevelLabel =
          appealLevels.find((level) => level.value === selectedAppealLevel)
            ?.label || selectedAppealLevel;

        if (existingArgument) {
          await deleteLienArgument({
            id: existingArgument.id,
          }).unwrap();

          const disputeLabel =
            disputeTypes.find(
              (type: ArgumentTypeChoices) => type.value === disputeType,
            )?.label || disputeType;
          KaboodleToaster.success(
            `${disputeLabel} dispute removed from ${appealLevelLabel}`,
          );
          return;
        }

        const response = await createLienArgument({
          data: {
            lien: lien.id,
            argument_type: disputeType,
            payment_history: paymentHistoryId,
            level_of_appeal: selectedAppealLevel,
          },
        }).unwrap();

        const disputeLabel =
          disputeTypes.find(
            (type: ArgumentTypeChoices) => type.value === disputeType,
          )?.label || disputeType;

        // check if there are more codes available for dispute
        // only show modal for UNRELATED, DENIED, NON_COVERED_SERVICE types
        const eligibleForDisputeAll = [
          "UNRELATED",
          "DENIED",
          "NON_COVERED_SERVICE",
        ].includes(disputeType);

        if (
          response.more_codes_available_for_dispute &&
          eligibleForDisputeAll &&
          paymentHistoryId
        ) {
          setPendingDisputeData({
            disputeType,
            paymentHistoryId,
          });
          setIsDisputeAllModalOpen(true);
          // don't show success toast yet wait for user decision
        } else {
          KaboodleToaster.success(
            `${disputeLabel} dispute added to ${appealLevelLabel}`,
          );
        }
      } catch (error) {
        console.error("API Error:", error);
        const errorMessage = error?.data?.detail || "Failed to create dispute";
        KaboodleToaster.error(errorMessage);
      }
    },
    [
      lien.id,
      lien.part_type,
      selectedAppealLevel,
      lienArgumentsData,
      createLienArgument,
      deleteLienArgument,
      disputeTypes,
      appealLevels,
    ],
  );

  const handleDisputeAllConfirm = useCallback(async () => {
    if (!pendingDisputeData) return;

    try {
      await createLienArgument({
        data: {
          lien: lien.id,
          argument_type: pendingDisputeData.disputeType,
          payment_history: pendingDisputeData.paymentHistoryId,
          level_of_appeal: selectedAppealLevel,
          dispute_all_matching_codes: true,
        },
      }).unwrap();

      const disputeLabel =
        disputeTypes.find(
          (type: ArgumentTypeChoices) =>
            type.value === pendingDisputeData.disputeType,
        )?.label || pendingDisputeData.disputeType;

      KaboodleToaster.success(
        `All matching ${disputeLabel} disputes created successfully`,
      );

      setPendingDisputeData(null);
    } catch (error) {
      console.error("API Error:", error);
      const errorMessage =
        error?.data?.detail ||
        "Failed to create disputes for all matching codes";
      KaboodleToaster.error(errorMessage);
    }
  }, [
    pendingDisputeData,
    createLienArgument,
    lien.id,
    selectedAppealLevel,
    disputeTypes,
  ]);

  const handleDisputeAllDecline = useCallback(() => {
    if (!pendingDisputeData) return;

    const disputeLabel =
      disputeTypes.find(
        (type: ArgumentTypeChoices) =>
          type.value === pendingDisputeData.disputeType,
      )?.label || pendingDisputeData.disputeType;

    const appealLevelLabel =
      appealLevels.find((level) => level.value === selectedAppealLevel)
        ?.label || selectedAppealLevel;

    KaboodleToaster.success(
      `${disputeLabel} dispute added to ${appealLevelLabel}`,
    );

    setPendingDisputeData(null);
  }, [pendingDisputeData, disputeTypes, appealLevels, selectedAppealLevel]);

  const renderDisputeSelection = useCallback(
    ({ row }: { row: DisplayRowType }) => {
      if (!isParentRow(row)) return null;

      return (
        <div className="select-liens">
          {disputeTypes
            .filter((type: ArgumentTypeChoices) => type.value !== null)
            .map((type: ArgumentTypeChoices) => {
              const hasArgument = lienArgumentsData?.results?.some(
                (arg: LienArgument) =>
                  arg.payment_history === row.rowId &&
                  arg.argument_type === type.value,
              );

              return (
                <DisputeIcon
                  key={type.value}
                  type={type}
                  hasArgument={hasArgument}
                  onDisputeClick={(disputeType) =>
                    handleDisputeClick(disputeType, row.rowId)
                  }
                />
              );
            })}
        </div>
      );
    },
    [disputeTypes, lienArgumentsData, handleDisputeClick, selectedAppealLevel],
  );

  const columns = useMemo<TableColumn[]>(
    () =>
      getPaymentHistoryColumns(lien.part_type).map((col) =>
        col.key === "disputes"
          ? { ...col, renderCell: renderDisputeSelection }
          : col.key === "pay_amount" || col.key === "dispute_amount"
            ? {
                ...col,
                renderCell: ({ row }) => {
                  if (!isParentRow(row)) return null;
                  const amount =
                    col.key === "pay_amount"
                      ? row.pay_amount
                      : row.dispute_amount;
                  return amount !== undefined && amount > 0
                    ? formatCurrency(amount)
                    : null;
                },
              }
            : col.key === "relationship_score"
              ? {
                  ...col,
                  renderCell: ({ row }) => {
                    const score = row.relationship_score;
                    const reason = row.related_reason;

                    return score !== "" && reason ? (
                      <Tooltip content={reason} place="left">
                        <div className="relationship-score-cell">{score}</div>
                      </Tooltip>
                    ) : (
                      <div className="relationship-score-cell">
                        {score !== "" ? score : null}
                      </div>
                    );
                  },
                }
              : col,
      ),
    [renderDisputeSelection, lien.part_type],
  );

  const handleRowClick = useCallback((row: DisplayRowType) => {
    if (row.originalRow) {
      setSelectedPaymentHistory(row.originalRow);
      setIsModalOpen(true);
    }
  }, []);

  const handleSubmitPaymentHistory = useCallback(
    async (formData: PaymentHistoryFormData) => {
      try {
        const processedData = {
          ...formData,
          diagnosis_codes: formData.diagnosis_codes.map((code) => ({
            code: code.code,
            indicator: code.indicator || "0",
          })),
          hcpcs: formData.hcpcs_codes.map((code) => code.code),
          cpt: formData.cpt_codes.map((code) => code.code),
          drg: formData.drg_codes.map((code) => code.code),
          ndc: formData.ndc_codes.map((code) => code.code),
          ndc_description: formData.ndc_codes.map(
            (code) => code.description || "",
          ),
          conditional_payment: Number(formData.conditional_payment) || 0,
          correct_all: formData.correct_all,
        };

        if (selectedPaymentHistory) {
          await updatePaymentHistory({
            id: selectedPaymentHistory.id,
            data: processedData,
          }).unwrap();
          KaboodleToaster.success("Lien item updated successfully");
        } else {
          await createPaymentHistory({
            data: {
              ...processedData,
              lien: lien.id,
              is_disputed: false,
              organization: lien.file.organization,
            },
          }).unwrap();
          KaboodleToaster.success("Lien item created successfully");
        }

        setIsModalOpen(false);
        setSelectedPaymentHistory(undefined);
      } catch (error) {
        console.error("API Error:", error);
        const errorMessage =
          error?.data?.detail ||
          `Failed to ${selectedPaymentHistory ? "update" : "create"} lien item`;
        KaboodleToaster.error(errorMessage);
      }
    },
    [
      createPaymentHistory,
      lien.id,
      selectedPaymentHistory,
      updatePaymentHistory,
    ],
  );

  const handleReportedDiagnosisCodesSave = useCallback(
    async (formData: ReportedDiagnosisCodesFormData) => {
      try {
        const fieldPrefix = selectedAppealLevel.toLowerCase();
        const diagnosisCodesKey = `${fieldPrefix}_reported_diagnosis_codes`;

        const processedData = {
          [diagnosisCodesKey]: formData.reported_diagnosis_codes.map(
            (code) => ({
              code: code.code,
              indicator: code.indicator || "0",
            }),
          ),
        };

        await patchLien({
          lienId,
          data: processedData,
        }).unwrap();
      } catch (error) {
        console.error("API Error:", error);
        const errorMessage =
          error?.data?.detail ||
          `Failed to ${selectedPaymentHistory ? "update" : "create"} lien item`;
        KaboodleToaster.error(errorMessage);
      }
    },
    [lienId, patchLien, selectedAppealLevel],
  );

  const handleDeletePaymentHistory = useCallback(
    async (id: number) => {
      try {
        await deletePaymentHistory({
          id,
        }).unwrap();
        setIsModalOpen(false);
        setSelectedPaymentHistory(undefined);
        KaboodleToaster.success("Lien item deleted successfully");
      } catch (error) {
        console.error("API Error:", error);
        const errorMessage =
          error?.data?.detail || "Failed to delete lien item";
        KaboodleToaster.error(errorMessage);
      }
    },
    [deletePaymentHistory],
  );

  // update probability when drawer opens
  useEffect(() => {
    if (showPaymentHistory) {
      setProbability(0);
    }
  }, [showPaymentHistory]);

  const { control } = useForm({
    defaultValues: {
      appeal_level: "CPL",
    },
  });

  const handleAppealLevelChange = async (option: OptionType) => {
    if (!option?.value) return; // don't allow clearing the selection
    setSelectedAppealLevel(option.value as string);
  };

  const currentDisputedAmount = useMemo(
    () => getDisputedAmountForAppealLevel(lien, selectedAppealLevel),
    [lien, selectedAppealLevel],
  );

  if (!lien) return null;
  if (isLoadingPaymentHistories) return <LoadingIcon />;
  if (!paymentHistories) return <ErrorState />;

  return (
    <div
      className={cx("documents documents-liens-history", {
        open: showPaymentHistory,
      })}
    >
      <div className="fixed-header">
        <div className="inline-stats">
          {lien.part_type === "AB" && (
            <div className="stat-item">
              <ControlledSelect
                name="appeal_level"
                label="Level of Appeal"
                control={control}
                options={appealLevels}
                errors={{}}
                onSave={handleAppealLevelChange}
                preventClear={true}
              />
            </div>
          )}
          <div className="stat-item">
            <MaskedInput
              name="total_disputed"
              label="Total Disputed"
              mask="dollar"
              value={currentDisputedAmount}
              readOnly
            />
          </div>
          <div className="stat-item">
            <MaskedInput
              name="cost_containment"
              label="Cost Containment Probability"
              value={probability.toString()}
              readOnly
              mask="number"
              childAfter={<span className="percentage">%</span>}
            />
          </div>
          <div className="stat-item stat-item--ai-status">
            <AIResultsStatus
              aiAnalyses={lien.ai_lien_analyses || null}
              selectedAppealLevel={selectedAppealLevel}
              lienId={lien.id}
            />
          </div>
        </div>
        <div className="reported-diagnosis-codes-descriptions-section">
          <span className="label">
            Reported Diagnosis Codes and Descriptions:
          </span>
          <ul className="code-list">
            {selectedReportedDiagnosisCodes?.map(
              ({ code, description, indicator }, idx) => {
                return (
                  <li key={idx}>
                    <Tooltip content={description} place="top">
                      <div
                        className="code-item"
                        onClick={() => {
                          // pass all existing codes to the modal, not just the clicked one
                          const allCodes =
                            selectedReportedDiagnosisCodes.map((item) => {
                              return {
                                code: item.code,
                                description: item.description || "",
                                indicator: item.indicator || "0",
                              };
                            }) || [];

                          setSelectedReportedDiagnosisCodes(allCodes);
                          setIsReportedDiagnosisCodesModalOpen(true);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {code} ({indicator === "9" ? "ICD-9" : "ICD-10"})
                      </div>
                    </Tooltip>
                  </li>
                );
              },
            )}
            <div className="add-button-wrapper">
              <ButtonIcon
                kind="neutral"
                size="small"
                onClick={() => {
                  // pass all existing codes to modal so new codes are added to existing ones
                  const allCodes =
                    selectedReportedDiagnosisCodes?.map((item) => {
                      return {
                        code: item.code,
                        description: item.description || "",
                        indicator: item.indicator || "0",
                      };
                    }) || [];

                  setSelectedReportedDiagnosisCodes(allCodes);
                  setIsReportedDiagnosisCodesModalOpen(true);
                }}
              >
                add_circle
              </ButtonIcon>
            </div>
          </ul>
          <ReportedDiagnosisCodesModal
            isOpen={isReportedDiagnosisCodesModalOpen}
            onClose={() => setIsReportedDiagnosisCodesModalOpen(false)}
            onSubmit={handleReportedDiagnosisCodesSave}
            initialData={selectedReportedDiagnosisCodes}
            title="Edit Reported Diagnosis Codes"
          />
          <DisputeAllModal
            isOpen={isDisputeAllModalOpen}
            onClose={() => setIsDisputeAllModalOpen(false)}
            onConfirm={handleDisputeAllConfirm}
            onDecline={handleDisputeAllDecline}
          />
        </div>
        <DisputeLegend disputeTypes={disputeTypes} />
      </div>

      <div className="documents-wrapper">
        <div className="header-primary flex jc-sb ai-c">
          <span>Lien Itemization</span>
          <ButtonIcon
            kind="neutral"
            size="small"
            onClick={() => {
              setSelectedPaymentHistory(undefined);
              setIsModalOpen(true);
            }}
          >
            add_circle
          </ButtonIcon>
        </div>
        <div className="table-container">
          <Table
            className="liens-payment-history"
            drawer={showPaymentHistory}
            columns={columns}
            rows={rows}
            rowKeyGetter={(row: DisplayRowType) => row.id}
            onCellClick={({ row }) => handleRowClick(row)}
            defaultColumnOptions={{
              resizable: true,
              minWidth: 80,
            }}
            isSortable={false}
            rowClass={(row: DisplayRowType) =>
              cx({
                "diagnostic-child-row": isChildRow(row),
                "diagnostic-parent-row": isParentRow(row),
                "invalid-codes-row": hasInvalidCodes(row),
              })
            }
            isLoading={isFetching}
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex: page[selectedAppealLevel],
              rowCount: paymentHistories.count,
              onPageChange: (page) =>
                setPage((prev) => ({ ...prev, [selectedAppealLevel]: page })),
              isLoading: isFetching,
            }}
          />
        </div>
      </div>

      <PaymentHistoryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPaymentHistory(undefined);
        }}
        onSubmit={handleSubmitPaymentHistory}
        onDelete={
          selectedPaymentHistory
            ? () => handleDeletePaymentHistory(selectedPaymentHistory.id)
            : undefined
        }
        initialData={selectedPaymentHistory}
        title={selectedPaymentHistory ? "Edit Lien Item" : "Add Lien Item"}
        levelOfAppeal={selectedAppealLevel}
        partType={lien.part_type}
      />
    </div>
  );
};

export default LienPaymentHistory;
